import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import MInputField from "@/components/ui/atoms/MInputField";
import { cn } from "@/utils/tailwind-utils";
import { t } from "i18next";
import styles from "./AutoCompleteInput.module.css";
import { IconX } from "@tabler/icons-react";

export interface AutoCompleteInputProps<T> {
  /**
   * The data array to search over.
   */
  data: T[];
  /**
   * Function to get the display label for each item.
   */
  getOptionLabel: (item: T) => string;
  /**
   * Called when an option is selected.
   */
  onSelect: (item: T | string) => void;
  /**
   * (Optional) Label for the input field.
   */
  label?: string;
  /**
   * (Optional) Placeholder for the input.
   */
  placeholder?: string;
  /**
   * (Optional) Controlled value for the input field.
   */
  value?: string;
  /**
   * (Optional) Called when the input value changes (for controlled mode).
   */
  onChange?: (value: string) => void;
  /**
   * (Optional) Minimum characters before showing suggestions.
   */
  minLength?: number;
  /**
   * (Optional) Custom className for the wrapper.
   */
  className?: string;
  /**
   * (Optional) Custom render for each option.
   */
  renderOption?: (item: T) => React.ReactNode;
  /**
   * (Optional) Called when the input value changes and >= minLength.
   */
  onSearch?: (query: string) => void;
  /**
   * (Optional) Whether the component is in a loading state.
   */
  loading?: boolean;
  /**
   * (Optional) Initial value for the input field.
   */
  initialValue?: string;
  /**
   * (Optional) Whether to allow custom text input that isn't in the options.
   */
  allowCustomInput?: boolean;
  /**
   * (Optional) Whether to show all options on focus, regardless of input length.
   */
  showAllOnFocus?: boolean;
  /**
   * (Optional) Callback to load more data for infinite scroll.
   */
  onLoadMore?: () => void;
  /**
   * (Optional) Whether there are more items to load.
   */
  hasMore?: boolean;
}

// Portal dropdown component
// CSS Module for AutoCompleteInput
const autocompleteStyles = {
  dropdown: "absolute z-[9999]",
  dropdownContent:
    "flex flex-col items-start gap-[var(--scale-padding-xxs,4px)] w-[345px] p-[var(--scale-padding-xs,8px)] rounded-[var(--scale-radius-sm,12px)] border border-[var(--colour-border-divider-n40,#E2E2E2)] bg-[var(--colour-background-surface-lvl-2,#FFF)] overflow-y-auto custom-scrollbar max-h-[200px]",
  dropdownOption:
    "flex items-center gap-[var(--scale-padding-xxs,4px)] self-stretch py-[var(--scale-padding-lg,16px)] px-[10px] cursor-pointer transition-all hover:bg-violet-50 rounded-lg",
};

function DropdownPortal({
  anchorRef,
  widthRef,
  children,
}: {
  anchorRef: React.RefObject<HTMLElement>;
  widthRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
}) {
  const [position, setPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const [mounted, setMounted] = React.useState(false);

  const updatePosition = React.useCallback(() => {
    const anchor = anchorRef.current as HTMLElement | null;
    const widthAnchor = widthRef.current as HTMLElement | null;
    if (anchor && widthAnchor) {
      const rect = anchor.getBoundingClientRect();

      // Find the nearest scrollable ancestor and its sticky header (if any),
      // so the dropdown never renders above that header.
      let minTopViewport = 0;
      let el: HTMLElement | null = anchor.parentElement;
      while (el && el !== document.body) {
        const overflowY = window.getComputedStyle(el).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") {
          // Look for a sticky direct child (e.g. MHeader)
          const stickyChild = Array.from(el.children).find(
            (child) =>
              window.getComputedStyle(child as HTMLElement).position ===
              "sticky"
          ) as HTMLElement | undefined;
          minTopViewport = stickyChild
            ? stickyChild.getBoundingClientRect().bottom
            : el.getBoundingClientRect().top;
          break;
        }
        el = el.parentElement;
      }

      setPosition({
        top: Math.max(rect.bottom, minTopViewport) + window.scrollY,
        left: rect.left + window.scrollX,
        width: widthAnchor.offsetWidth,
      });
      setMounted(true);
    }
  }, [anchorRef, widthRef]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    // Add scroll listener to update position when user scrolls
    const handleScroll = () => {
      updatePosition();
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [updatePosition]);

  // Use CSS classes with dynamic values via style attribute
  // This is a valid exception to the "no inline styles" rule since these values
  // must be calculated dynamically and can't be predetermined with Tailwind classes
  return (
    <div
      className={`${autocompleteStyles.dropdown} ${
        mounted ? "opacity-100" : "opacity-0"
      }`}
      style={{
        // These specific positioning values must be inline as they're dynamically calculated
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
      }}
    >
      {children}
    </div>
  );
}

export function AutoCompleteInput<T extends object>(
  props: AutoCompleteInputProps<T> &
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">
) {
  const {
    data,
    getOptionLabel,
    onSelect,
    label,
    placeholder,
    value: controlledValue,
    onChange,
    minLength = 2,
    className,
    renderOption,
    onSearch,
    loading,
    initialValue = "",
    allowCustomInput = false,
    showAllOnFocus = false,
    onLoadMore,
    hasMore = false,
    readOnly = false,
    ...inputProps
  } = props;

  // --- function body continues here ---
  const [inputValue, setInputValue] = useState(
    controlledValue || initialValue || ""
  );
  const [showOptions, setShowOptions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setInputValue(controlledValue || "");
  }, [controlledValue]);

  // Track if user has made a selection
  const [hasSelected, setHasSelected] = useState(
    Boolean(initialValue || controlledValue)
  );

  // Track if the input is currently focused
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!onSearch) return;
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    // Don't show options if user has made a selection and is not actively typing
    // or if the input is not focused
    if ((hasSelected && inputValue === controlledValue) || !isFocused) {
      setShowOptions(false);
      return;
    }

    // Show options in these cases:
    // 1. User is typing and meets minLength requirement
    // 2. showAllOnFocus is true and input is focused (even with empty input)
    if (inputValue.length >= minLength || (showAllOnFocus && isFocused)) {
      debounceTimeout.current = setTimeout(() => {
        // Always search with current input value to maintain filtering
        onSearch(inputValue);
        setShowOptions(true);
      }, 300);
    } else {
      setShowOptions(false);
    }

    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [
    inputValue,
    minLength,
    onSearch,
    hasSelected,
    showAllOnFocus,
    isFocused,
    controlledValue,
  ]);

  const handleClear = () => {
    setInputValue("");
    setHasSelected(false);
    setShowOptions(false);
    setIsFocused(false);
    onChange?.("");
    onSelect?.("");
  };

  // Handle input changes when user is typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Reset hasSelected in these cases:
    // 1. Input is empty (user deleted everything)
    // 2. Input value completely changed (not just appending/modifying)
    const isEmptyOrNewValue =
      newValue === "" ||
      (hasSelected &&
        !newValue.startsWith(controlledValue || "") &&
        !controlledValue?.startsWith(newValue));

    if (isEmptyOrNewValue) {
      setHasSelected(false); // User is typing a new value or cleared the input
    }

    onChange?.(newValue);

    // Hide options if input is cleared
    if (newValue === "") {
      setShowOptions(false);
      return;
    }

    // If custom input is allowed, update the selected value as the user types
    if (allowCustomInput) {
      onSelect(newValue);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowOptions(false);
        setIsFocused(false);

        // When clicking outside, if we have a value and allowCustomInput is true,
        // consider the current input value as selected to prevent dropdown from showing again
        if (allowCustomInput && inputValue.trim() !== "") {
          setHasSelected(true);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [allowCustomInput, inputValue]);

  const handleInputFocus = () => {
    setIsFocused(true);

    // Don't show options if user has made a selection
    if (hasSelected) {
      return;
    }

    // When input gets focus
    if (showAllOnFocus) {
      // If onSearch is provided, call it with current input value
      // This preserves any filtering the user has already done
      if (onSearch) {
        onSearch(inputValue);
      }
      setShowOptions(true);
    }
    // Otherwise, only show options if the input value meets the minimum length
    else if (inputValue.length >= minLength) {
      setShowOptions(true);
    }
  };

  // Prevent dropdown from reopening after select
  const suppressNextBlur = useRef(false);

  const handleSelect = (item: T) => {
    const label = getOptionLabel(item);
    setInputValue(label);
    setShowOptions(false);
    setHasSelected(true); // Mark that user has made a selection
    setIsFocused(false); // Reset focus state
    onSelect(item);
    if (onChange) onChange(label); // Ensure parent controlled value updates
    suppressNextBlur.current = true;
    // Blur the input to prevent focus events from reopening the dropdown
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // Prevent dropdown from reopening on blur after select
  const handleInputBlur = () => {
    if (suppressNextBlur.current) {
      suppressNextBlur.current = false;
      return;
    }
    setIsFocused(false);
    setShowOptions(false);

    // When blurring, if we have a value and allowCustomInput is true,
    // consider the current input value as selected to prevent dropdown from showing again
    if (allowCustomInput && inputValue.trim() !== "") {
      setHasSelected(true);
    }
  };

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      <MInputField
        label={label}
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
        variant="Underlined"
        size="md"
        showLabel={!!label}
        clearInput
        {...(inputProps as any)}
      />
      {inputValue && !readOnly && (
        <button
          type="button"
          aria-label="Clear input"
          onClick={handleClear}
          className={styles.clearButton}
        >
          <IconX size={18} stroke={1.8} />
        </button>
      )}
      {showOptions &&
        data.length > 0 &&
        typeof window !== "undefined" &&
        (() => {
          return createPortal(
            <DropdownPortal anchorRef={wrapperRef} widthRef={wrapperRef}>
              <div
                ref={dropdownRef}
                className={`${autocompleteStyles.dropdownContent} ${styles.dropdownContent}`}
                role="listbox"
                aria-label="Options"
                onScroll={(e) => {
                  if (!onLoadMore || !hasMore || loading) return;

                  const target = e.currentTarget;
                  const scrolledToBottom =
                    target.scrollHeight - target.scrollTop <=
                    target.clientHeight + 50; // 50px threshold

                  if (scrolledToBottom) {
                    onLoadMore();
                  }
                }}
              >
                {loading && (
                  <div className="px-6 py-4 text-[var(--text-navigational-n600)] text-base">
                    {t("common.loading")}
                  </div>
                )}
                {!loading && data.length === 0 && !allowCustomInput && (
                  <div className="px-6 py-4 text-[var(--text-navigational-n600)] text-base">
                    {t("common.noResults")}
                  </div>
                )}
                {data.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <div
                      className={autocompleteStyles.dropdownOption}
                      onMouseDown={() => {
                        console.log(
                          "[AutoCompleteInput] dropdown item clicked",
                          item
                        );
                        handleSelect(item);
                      }}
                      tabIndex={0}
                      role="option"
                      aria-selected="false"
                    >
                      <div className="flex-1 text-[var(--text-navigational-n600)] truncate">
                        {renderOption
                          ? renderOption(item)
                          : getOptionLabel(item)}
                      </div>
                    </div>
                    {idx < data.length - 1 && (
                      <div className={styles.optionDivider} />
                    )}
                  </React.Fragment>
                ))}
                {loading && data.length > 0 && (
                  <div className="px-6 py-2 text-center text-sm text-[var(--text-navigational-n600)]">
                    {t("common.loading")}...
                  </div>
                )}
              </div>
            </DropdownPortal>,
            document.body
          );
        })()}
    </div>
  );
}

export default AutoCompleteInput;
